// Follows the Web Production Standard
// https://github.com/Skyscanner/production-standards/blob/master/web/required/dates-timezone-library.md
// Currently is duplicated on both Webapp and Server until /common can support importing npm packages in its files
import { DATE_FORMATS } from '@web-platform/shared-universal-constants/src/dateFormats';
import { parseISO } from 'date-fns/parseISO';
import Localisation, {
  currency as saddlebagCurrency,
  date as saddlebagDate,
} from 'saddlebag-localisation';

import type {
  ContextCulture as Culture,
  ContextCldr as Cldr,
} from '@web-platform/shared-universal-types/src/context';
import type { DateString, WeekDay } from '@web-platform/shared-universal-types/src/dateString';
import type {
  DateTime,
  FullDomainName,
} from '@web-platform/shared-universal-types/src/localisation';

type FormatCurrency = (value: number) => string;
type FormatCurrencyShort = (
  value: number,
  currencyCode: string,
  options?: {
    minimumSignificantDigits?: number;
    maximumSignificantDigits?: number;
    compact?: 'short' | 'long';
  },
) => string;
type FormatDate = (datetime: DateTime, format?: string) => string;
type FormatDateCustom = (datetime: DateTime, datePattern: string) => string;
type FormatTime = (datetime: DateTime, format?: string) => string;
type FormatDateTime = (datetime: DateTime, format: string) => string;
type FormatDateNonlocalised = (datetime: DateTime, format?: string) => DateString;
type FormatNumber = (value: number, options?: Intl.NumberFormatOptions) => string;

let localisation: {
  getDaysOfWeek: () => WeekDay[];
  getMonthName: (date: DateTime) => string;
  getFirstDay: () => number;
  formatCurrency: FormatCurrency;
  formatCurrencyShort: FormatCurrencyShort;
  formatDate: FormatDate;
  formatTime: FormatTime;
  formatDateTime: FormatDateTime;
  formatNumber: FormatNumber;
  formatDateNonlocalised: FormatDateNonlocalised;
  formatDateCustom: FormatDateCustom;
  monthNames: (format?: string) => String[];
};

let cldr: Cldr;
let culture: Culture;
let fullDomainName: FullDomainName;

// Cached formatter instances — materialised once after setCldr/setCulture,
// invalidated when locale data changes. Avoids recreating Globalize currency
// formatters and Intl.NumberFormat objects on every render.
const MAX_CURRENCY_CACHE_SIZE = 200;
const MAX_NUMBER_FORMATTER_CACHE_SIZE = 50;
const cachedCurrencyResults = new Map<number, string>();
const cachedNumberFormatters = new Map<string, Intl.NumberFormat>();

const invalidateFormatterCaches = () => {
  cachedCurrencyResults.clear();
  cachedNumberFormatters.clear();
};

// FullDomainName
const setFullDomainName = (fullDomainNameStr: FullDomainName) => {
  fullDomainName = fullDomainNameStr;
};
const getFullDomainName = (): FullDomainName => fullDomainName;

// CLDR
const setCldr = (cldrData: Cldr) => {
  Localisation.enhance(saddlebagCurrency, saddlebagDate);
  localisation = Localisation(cldrData);
  cldr = { ...cldrData };
  invalidateFormatterCaches();
};
const getCldr = (): Cldr => cldr;

// Culture
const setCulture = (cultureData: Culture) => {
  culture = { ...cultureData };
  invalidateFormatterCaches();
};
const getCulture = (): Culture => culture;

// parsing with date-fns
const parseDateTime = (datetime: DateTime): Date => {
  if (typeof datetime === 'string') {
    return parseISO(datetime);
  }
  return datetime;
};

// getters
const getDaysOfWeek = () => localisation.getDaysOfWeek();
const getMonthName = (date: DateTime): string => localisation.getMonthName(parseDateTime(date));
const getFirstDay = (): number => localisation.getFirstDay();
const getYearMonthDay = (dateStr: DateString) => {
  const date = parseDateTime(dateStr);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};
const getMonthStrings = (format?: string): String[] => localisation.monthNames(format);
const isMonthDate = (dateStr: DateString | undefined) => {
  if (!dateStr) return false;

  return dateStr === formatDateNonlocalised(dateStr, DATE_FORMATS.YEAR_MONTH_FULL_DASHED);
};

const isSpecificDate = (dateStr: DateString | undefined) => {
  if (!dateStr) return false;

  return dateStr === formatDateNonlocalised(dateStr, DATE_FORMATS.YEAR_MONTH_DAY_DASHED);
};

// formatters
const formatCurrency: FormatCurrency = (amount) => {
  const cached = cachedCurrencyResults.get(amount);
  if (cached !== undefined) return cached;
  const result = localisation.formatCurrency(amount);
  if (cachedCurrencyResults.size >= MAX_CURRENCY_CACHE_SIZE) {
    cachedCurrencyResults.clear();
  }
  cachedCurrencyResults.set(amount, result);
  return result;
};

const formatCurrencyShort: FormatCurrencyShort = (amount, currencyCode, options) =>
  localisation.formatCurrencyShort(amount, currencyCode, options);

const formatDate: FormatDate = (datetime, format) =>
  localisation.formatDate(parseDateTime(datetime), format);

const formatDateCustom: FormatDateCustom = (datetime, datePattern) =>
  localisation.formatDateCustom(parseDateTime(datetime), datePattern);

const formatTime: FormatTime = (datetime, format) =>
  localisation.formatTime(parseDateTime(datetime), format);

const formatDateTime: FormatDateTime = (datetime, format) =>
  localisation.formatDateTime(parseDateTime(datetime), format);

const formatDateNonlocalised: FormatDateNonlocalised = (
  datetime,
  format = DATE_FORMATS.YEAR_MONTH_DAY_DASHED,
) => localisation.formatDateNonlocalised(parseDateTime(datetime), format);

const formatNumber: FormatNumber = (value: number, options: Intl.NumberFormatOptions = {}) => {
  const key = JSON.stringify(options);
  let formatter = cachedNumberFormatters.get(key);
  if (!formatter) {
    formatter = new Intl.NumberFormat(culture.locale, options);
    if (cachedNumberFormatters.size >= MAX_NUMBER_FORMATTER_CACHE_SIZE) {
      cachedNumberFormatters.clear();
    }
    cachedNumberFormatters.set(key, formatter);
  }
  return formatter.format(value);
};

export {
  // singletons
  setCldr,
  getCldr,
  setCulture,
  getCulture,
  setFullDomainName,
  getFullDomainName,
  // getters
  getDaysOfWeek,
  getMonthName,
  getFirstDay,
  getYearMonthDay,
  getMonthStrings,
  isMonthDate,
  isSpecificDate,
  // formatters
  formatCurrency,
  formatDate,
  formatDateCustom,
  formatTime,
  formatDateTime,
  formatNumber,
  formatDateNonlocalised,
  formatCurrencyShort,
  // parsers
  parseDateTime,
};
